import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { clearTokens, getCurrentUserProfile, getProfilePictureUrl } from '../services/api';
import api, { formatApiError, getWorkflows, getTenantSubdomain } from '../services/api';
import { getImputationEtatOptions } from '../utils/imputationEtats';
import AdminTopBar from './AdminTopBar';
import Toast from './Toast';
import useToast from '../hooks/useToast';

// Preferred workflow order for display (GCR first, BYNOW last) — used for multi-insert default
const WORKFLOW_DISPLAY_ORDER = ['GCR', 'GIR', 'PROMO', 'BYNOW'];
const sortWorkflowCodes = (codes) => {
  if (!codes || !codes.length) return codes || [];
  return [...codes].sort((a, b) => {
    const ia = WORKFLOW_DISPLAY_ORDER.indexOf(a);
    const ib = WORKFLOW_DISPLAY_ORDER.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return (a || '').localeCompare(b || '');
  });
};

const EditImputationPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  
  // Current user data state
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  
  // State for operations and imputation
  const [operations, setOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [loadingImputation, setLoadingImputation] = useState(true);
  const [imputation, setImputation] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const allWorkflowCodes = sortWorkflowCodes(workflows.map(w => w.code).filter(Boolean));

  // State for form data
  const [formData, setFormData] = useState({
    date_operation: '',
    gcr_prefix: 'GCR',
    gcr_code: '',
    operation: '',
    etat_final: '',
    commentaire: '',
    modification_reason: ''
  });

  // State for domain filter (filters Operation Code options)
  const [selectedDomain, setSelectedDomain] = useState('');
  // State for production line filter (filters Operation Code options)
  const [selectedProductionLine, setSelectedProductionLine] = useState('');

  // State for operation search
  const [operationSearch, setOperationSearch] = useState('');
  const [showOperationDropdown, setShowOperationDropdown] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);

  // State for notifications
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { toast, showSuccess, showWarning, showError, showInfo, hideToast } = useToast();

  // Refs
  const operationRef = useRef(null);

  // Project code: type-specific prefix and max digits (loaded from workflows)
  const projectCodePrefixByType = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = m[w.code] || (w.project_code || '').trim(); });
    return Object.keys(m).length ? m : { GCR: 'INT-0000', GIR: 'TI000000', PROMO: 'T', BYNOW: 'INC' };
  }, [workflows]);
  const projectCodeMaxLengthByType = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = Math.max(0, Math.min(20, parseInt(w.digits_in_project_code, 10) || 0)); });
    return Object.keys(m).length ? m : { GCR: 7, GIR: 7, PROMO: 5, BYNOW: 10 };
  }, [workflows]);
  const workflowLabelByPrefix = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = (w.label || w.code).trim(); });
    return Object.keys(m).length ? m : { GCR: 'GCR', GIR: 'TT GIR', PROMO: 'PROMO', BYNOW: 'BYNOW' };
  }, [workflows]);
  const allowAlphabeticByPrefix = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = w.allow_alphabetic_chars !== false; });
    return Object.keys(m).length ? m : { GCR: true, GIR: true, PROMO: true, BYNOW: true };
  }, [workflows]);
  const getProjectCodeMaxLength = (prefix) => projectCodeMaxLengthByType[prefix] ?? 7;
  const getZeroProjectCode = (prefix) => '0'.repeat(getProjectCodeMaxLength(prefix));
  const isProjectCodeOptionalForPrefix = (prefix) => getProjectCodeMaxLength(prefix) === 0;

  // État choices (tenant-specific). Include current value if API returns a legacy code not in the list.
  const baseEtatChoices = React.useMemo(() => getImputationEtatOptions(getTenantSubdomain()), []);
  const etatChoices = formData.etat_final && !baseEtatChoices.some(c => c.value === formData.etat_final)
    ? [...baseEtatChoices, { value: formData.etat_final, label: imputation?.etat_final_display || formData.etat_final }]
    : baseEtatChoices;

  // When reference is disabled for this operation, employees cannot change project/workflow (keep existing gcr)
  const isReferenceDisabledForEmployee = currentUser?.role === 'EMPLOYE' && selectedOperation?.is_reference_enabled === false;
  // Workflows allowed for operation: if operation has workflows assigned, show those; else all workflows from DB
  const allowedWorkflows = selectedOperation?.allowed_workflow_codes;
  const hasWorkflowsAssigned = allowedWorkflows && allowedWorkflows.length > 0;
  const workflowOptions = sortWorkflowCodes(hasWorkflowsAssigned ? allowedWorkflows : (allWorkflowCodes.length ? allWorkflowCodes : ['GCR', 'GIR', 'PROMO', 'BYNOW']));
  // When no workflow is set for the operation, hide workflow and project code (backend will accept gcr "N/A")
  const shouldHideWorkflowAndProjectCode = isReferenceDisabledForEmployee || (selectedOperation && !hasWorkflowsAssigned);

  // Unique domains from operations (for Domain dropdown)
  const availableDomains = React.useMemo(() => {
    const domainSet = new Set();
    operations.forEach(op => {
      if (op.domain && String(op.domain).trim()) domainSet.add(op.domain.trim());
    });
    return Array.from(domainSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [operations]);

  // Operations filtered by selected domain (MULTI-DOMAINE appears for any domain)
  const operationsFilteredByDomain = React.useMemo(() => {
    if (!selectedDomain) return operations;
    return operations.filter(op => {
      const d = op.domain ? String(op.domain).trim() : '';
      if (!d) return false;
      if (d.toUpperCase().includes('MULTI-DOMAINE')) return true;
      return d === selectedDomain;
    });
  }, [operations, selectedDomain]);

  // Unique production lines from domain-filtered operations (for PL filter)
  const availableProductionLines = React.useMemo(() => {
    const plSet = new Set();
    operationsFilteredByDomain.forEach(op => {
      if (op.production_line_lp && String(op.production_line_lp).trim()) plSet.add(op.production_line_lp.trim());
    });
    return Array.from(plSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [operationsFilteredByDomain]);

  // Operations filtered by domain + production line
  const operationsFilteredByDomainAndPL = React.useMemo(() => {
    if (!selectedProductionLine) return operationsFilteredByDomain;
    return operationsFilteredByDomain.filter(op =>
      op.production_line_lp && String(op.production_line_lp).trim() === selectedProductionLine
    );
  }, [operationsFilteredByDomain, selectedProductionLine]);

  // Filter operations based on search; MULTI-DOMAINE operations always appear (not filtered by search)
  const getFilteredOperations = (searchTerm) => {
    const baseOps = operationsFilteredByDomainAndPL;
    if (!searchTerm.trim()) return baseOps;
    const term = searchTerm.toLowerCase();
    const matching = baseOps.filter(op =>
      op.production_line_lp?.toLowerCase().includes(term) || op.activity?.toLowerCase().includes(term)
    );
    const multiDomaine = baseOps.filter(op =>
      op.domain && String(op.domain).toUpperCase().includes('MULTI-DOMAINE')
    );
    const seen = new Set();
    return [...matching, ...multiDomaine].filter(op => {
      if (seen.has(op.id)) return false;
      seen.add(op.id);
      return true;
    });
  };

  // Fetch operations
  const fetchOperations = async () => {
    setLoadingOperations(true);
    try {
      const response = await api.getOperations({ status: 'active' }, 1, 1000);
      setOperations(response.results || []);
    } catch (error) {
      console.error('Error fetching operations:', error);
    } finally {
      setLoadingOperations(false);
    }
  };

  // Fetch imputation
  const fetchImputation = async () => {
    if (!id) return;
    setLoadingImputation(true);
    try {
      const data = await api.getImputation(parseInt(id));
      setImputation(data);
      
      // Parse GCR: type prefix + code prefix + type-specific digit suffix (e.g. GCR-INT-00001234567 → suffix 1234567)
      let gcrPrefix = 'GCR';
      let gcrCode = '';
      const validPrefixes = allWorkflowCodes.length ? allWorkflowCodes : ['GCR', 'GIR', 'PROMO', 'BYNOW'];
      if (data.gcr && data.gcr.includes('-')) {
        const firstDash = data.gcr.indexOf('-');
        const parts = [data.gcr.slice(0, firstDash), data.gcr.slice(firstDash + 1)];
        if (validPrefixes.includes(parts[0])) {
          gcrPrefix = parts[0];
          const rest = parts[1];
          const codePrefix = projectCodePrefixByType[gcrPrefix] || '';
          if (codePrefix && rest.startsWith(codePrefix)) {
            gcrCode = rest.slice(codePrefix.length);
          } else {
            gcrCode = rest;
          }
        } else {
          gcrCode = data.gcr;
        }
      } else if (data.gcr) {
        gcrCode = data.gcr;
      }

      const selectedOp = operations.find(op => op.id === data.operation?.id) || data.operation;
      const opDomain = selectedOp?.domain ? String(selectedOp.domain).trim() : '';
      const opPL = selectedOp?.production_line_lp ? String(selectedOp.production_line_lp).trim() : '';

      setFormData({
        date_operation: data.date_operation || '',
        gcr_prefix: gcrPrefix,
        gcr_code: gcrCode,
        operation: data.operation?.id?.toString() || '',
        etat_final: data.etat_final || '',
        commentaire: data.commentaire || '',
        modification_reason: ''
      });

      setSelectedDomain(opDomain);
      setSelectedProductionLine(opPL);
      setOperationSearch(selectedOp ? `${selectedOp.production_line_lp} · ${selectedOp.activity}` : '');
      setSelectedOperation(selectedOp);
    } catch (error) {
      console.error('Error fetching imputation:', error);
      showError(t('imputations.loadFailedTitle'), t('imputations.loadFailedRedirect'));
      setTimeout(() => navigate('/employee-dashboard/imputations'), 2000);
    } finally {
      setLoadingImputation(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (operationRef.current && !operationRef.current.contains(event.target)) {
        setShowOperationDropdown(false);
      }
    };

    if (showOperationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOperationDropdown]);

  // Fetch data on mount: operations and workflows in parallel
  useEffect(() => {
    fetchOperations();
  }, []);

  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const data = await getWorkflows();
        const list = Array.isArray(data) ? data : (data?.results ?? []);
        setWorkflows(list);
      } catch {
        setWorkflows([]);
      }
    };
    loadWorkflows();
  }, []);

  // Fetch imputation when operations are ready (workflows load in parallel; parse uses fallback until workflows load)
  useEffect(() => {
    if (operations.length > 0) {
      fetchImputation();
    }
  }, [id, operations.length]);

  // When domain changes: clear selected production line if it's no longer in available list
  useEffect(() => {
    if (selectedProductionLine && availableProductionLines.length > 0 && !availableProductionLines.includes(selectedProductionLine)) {
      setSelectedProductionLine('');
    }
  }, [selectedDomain, availableProductionLines]);

  // When domain or production line changes: clear selected operation if it no longer belongs to filtered list
  useEffect(() => {
    if (selectedOperation) {
      const belongsToFilter = operationsFilteredByDomainAndPL.some(op => op.id === selectedOperation.id);
      if (!belongsToFilter) {
        setSelectedOperation(null);
        setFormData(prev => ({ ...prev, operation: '' }));
        setOperationSearch('');
      }
    }
  }, [selectedDomain, selectedProductionLine, selectedOperation, operationsFilteredByDomainAndPL]);

  // Show info/warning toast based on editability
  useEffect(() => {
    if (imputation) {
      const editable = canEdit();
      if (editable) {
        showInfo(t('imputations.modificationAuthorized'), t('imputations.modificationAuthorizedBody'), 6000);
      } else {
        showWarning(t('imputations.modificationRestricted'), t('imputations.modificationRestrictedBody'), 6000);
      }
    }
  }, [imputation]);

  // Fetch current user data on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoadingUser(true);
        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        setCurrentUser({
          first_name: '',
          last_name: '',
          email: '',
          role: 'EMPLOYE'
        });
      } finally {
        setLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Check if imputation can be edited (less than 24 hours)
  const canEdit = () => {
    if (!imputation?.date_saisie) return false;
    const saisieDate = new Date(imputation.date_saisie);
    const now = new Date();
    const hoursDiff = (now - saisieDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imputation) return;

    setSaving(true);
    setFormErrors({});

    // Validate
    if (!formData.date_operation) {
      setFormErrors({ date_operation: t('imputations.dateRequired') });
      setSaving(false);
      return;
    }
    if (!formData.operation) {
      setFormErrors({ operation: t('imputations.operationRequired') });
      setSaving(false);
      return;
    }

    let gcr = imputation.gcr || '';
    const skipProjectCode = shouldHideWorkflowAndProjectCode;
    if (!skipProjectCode) {
      const maxLen = getProjectCodeMaxLength(formData.gcr_prefix);
      const digits = (formData.gcr_code || '').replace(/\D/g, '');
      const isOptional = isProjectCodeOptionalForPrefix(formData.gcr_prefix);
      if (!isOptional && !digits) {
        setFormErrors({ gcr: t('imputations.projectCodeRequired', { count: maxLen }) });
        setSaving(false);
        return;
      }
      const padded = maxLen > 0 ? digits.slice(0, maxLen).padStart(maxLen, '0') : '';
      if (!isOptional && padded === getZeroProjectCode(formData.gcr_prefix)) {
        setFormErrors({ gcr: t('imputations.projectCodeAllZeros') });
        setSaving(false);
        return;
      }
      const codePrefix = projectCodePrefixByType[formData.gcr_prefix] || '';
      const fullCode = `${codePrefix}${padded}`;
      gcr = `${formData.gcr_prefix}-${fullCode}`;
    } else {
      gcr = 'N/A';
    }

    try {
      const imputationData = {
        date_operation: formData.date_operation,
        gcr: gcr,
        operation_id: parseInt(formData.operation),
        etat_final: formData.etat_final,
        commentaire: formData.commentaire || ''
      };

      if (formData.modification_reason) {
        imputationData.modification_reason = formData.modification_reason;
      }

      await api.updateImputation(imputation.id, imputationData);
      
      // Show success toast
      showSuccess(t('imputations.entryUpdated'), t('imputations.successUpdated'));
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/employee-dashboard/imputations');
      }, 1500);
    } catch (error) {
      const formattedError = formatApiError(error);
      setFormErrors(formattedError.fieldErrors || {});
      
      // Show error toast
      const errorMessage = formattedError.generalError || t('imputations.updateFailedMsg');
      showError(t('imputations.updateFailed'), errorMessage);
      
      // Show API warnings if present
      if (error.response?.status === 429) {
        showWarning(t('imputations.rateLimit'), t('imputations.rateLimitMsg'), 5000);
      } else if (error.response?.status >= 500) {
        showWarning(t('imputations.serverError'), t('imputations.serverErrorMsg'), 5000);
      }
    } finally {
      setSaving(false);
    }
  };


  if (!imputation) {
    return (
      <div className="flex flex-col h-screen overflow-hidden bg-background">
        <AdminTopBar
          title={t('imputations.editTitle')}
          showSearch={false}
          showMenuButton={false}
        />
        {/* Main Content */}
        <main className="flex-1 flex flex-col h-full overflow-hidden bg-background relative">
          <div className="flex-1 overflow-y-auto p-8 scroll-smooth">
            <div className="max-w-4xl mx-auto flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <p className="text-red-600">{t('imputations.notFound')}</p>
                <button
                  onClick={() => navigate('/employee-dashboard/imputations')}
                  className="mt-4 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-hover transition-colors"
                >
                  {t('imputations.backToList')}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const editable = canEdit();

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      <AdminTopBar
        title={t('imputations.editTitle')}
        showSearch={false}
        showMenuButton={false}
      />
      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-background relative">
        <div className="flex-1 overflow-y-auto p-8 scroll-smooth">
          <div className="max-w-4xl mx-auto flex flex-col gap-6">
            {/* Page Heading */}
            <div className="flex flex-wrap justify-between items-end gap-4 px-1">
              <div className="flex min-w-72 flex-col gap-2">
                <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
                  {t('imputations.editTitleWithId', { id: imputation.id })}
                </h1>
                <p className="text-slate-500 text-base font-normal leading-normal">
                  {t('imputations.editSubtitle')}
                </p>
              </div>
              <button
                onClick={() => navigate('/employee-dashboard/imputations')}
                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 text-slate-700 hover:bg-slate-100 text-sm font-medium transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                <span>{t('imputations.backToList')}</span>
              </button>
            </div>

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />

      {/* Form Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-6 md:p-8">
          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            {/* Info Message */}
            {editable ? (
              <div className="p-4 bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-700/50 rounded-lg">
                <div className="flex items-start">
                  <span className="material-symbols-outlined text-[20px] text-blue-600 mt-0.5 mr-3">info</span>
                  <div>
                    <p className="text-sm font-medium text-blue-800">{t('imputations.modificationAuthorized')}</p>
                    <p className="text-xs text-blue-700 mt-1">{t('imputations.modificationAuthorizedBody')}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 dark:bg-amber-900/30 dark:border-amber-700/50 rounded-lg">
                <div className="flex items-start">
                  <span className="material-symbols-outlined text-[20px] text-amber-600 mt-0.5 mr-3">warning</span>
                  <div>
                    <p className="text-sm font-medium text-amber-800">{t('imputations.modificationRestricted')}</p>
                    <p className="text-xs text-amber-700 mt-1">{t('imputations.modificationRestrictedBody')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Messages */}
            {Object.keys(formErrors).length > 0 && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                {Object.values(formErrors).flat().join(', ')}
              </div>
            )}

            {/* Row 1: Date */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-900">{t('imputations.dateOfActivity')}</label>
              <div className="relative flex items-center group">
                <input
                  type="date"
                  value={formData.date_operation}
                  onChange={(e) => setFormData({ ...formData, date_operation: e.target.value })}
                  required
                  disabled={!editable}
                  className={`w-full h-12 rounded-lg border ${
                    formErrors.date_operation ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                  } px-4 text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none ${
                    !editable ? 'bg-slate-50 cursor-not-allowed' : ''
                  }`}
                />
                <div className="absolute right-4 text-slate-500 pointer-events-none group-focus-within:text-primary transition-colors">
                  <span className="material-symbols-outlined">calendar_today</span>
                </div>
              </div>
              {formErrors.date_operation && (
                <p className="text-red-600 text-xs mt-1">{formErrors.date_operation[0]}</p>
              )}
            </div>

            {/* Row 2: Domain (filters Operation Code) */}
            {availableDomains.length > 0 && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900">{t('imputations.domain')}</label>
                <div className={`relative flex flex-wrap gap-2 w-full rounded-lg bg-slate-50 p-1 border border-slate-200 ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                }`}>
                  {['', ...availableDomains].map((domain) => (
                    <label
                      key={domain || '__all__'}
                      className={`flex-1 min-w-[100px] h-10 relative ${!editable ? 'cursor-not-allowed' : 'cursor-pointer'} whitespace-nowrap`}
                      style={{ whiteSpace: 'nowrap' }} // For extra assurance across environments
                    >
                      <input
                        type="radio"
                        name="domain"
                        value={domain}
                        checked={selectedDomain === domain}
                        onChange={() => editable && setSelectedDomain(domain)}
                        disabled={!editable}
                        className="peer sr-only"
                      />
                      <div className="relative flex h-full w-full items-center justify-center rounded-md text-sm font-medium text-slate-500 transition-colors peer-checked:text-primary whitespace-nowrap">
                        {selectedDomain === domain && (
                          <motion.div
                            layoutId="editDomainIndicator"
                            className="absolute inset-0 rounded-md bg-white shadow-sm"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="relative z-10 whitespace-nowrap">{domain || t('imputations.allDomains')}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">{t('imputations.filterByDomain')}</p>
              </div>
            )}

            {/* Row 2.5: PL (Production Line) filter */}
            {availableProductionLines.length > 0 && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900">{t('imputations.plFilter')}</label>
                <div className={`relative flex flex-wrap gap-2 w-full rounded-lg bg-slate-50 p-1 border border-slate-200 ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                }`}>
                  {['', ...availableProductionLines].map((pl) => (
                    <label key={pl || '__all__'} className={`flex-1 min-w-[100px] h-10 relative ${!editable ? 'cursor-not-allowed' : 'cursor-pointer'} whitespace-nowrap`}>
                      <input
                        type="radio"
                        name="production_line"
                        value={pl}
                        checked={selectedProductionLine === pl}
                        onChange={() => editable && setSelectedProductionLine(pl)}
                        disabled={!editable}
                        className="peer sr-only"
                      />
                      <div className="relative flex h-full w-full items-center justify-center rounded-md text-sm font-medium text-slate-500 transition-colors peer-checked:text-primary whitespace-nowrap">
                        {selectedProductionLine === pl && (
                          <motion.div
                            layoutId="editPlIndicator"
                            className="absolute inset-0 rounded-md bg-white shadow-sm"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="relative z-10 whitespace-nowrap">{pl || t('imputations.allPL')}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">{t('imputations.filterByPL')}</p>
              </div>
            )}

            {/* Row 3: Operation Search */}
            <div className="flex flex-col gap-2 relative" ref={operationRef}>
              <label className="text-sm font-semibold text-slate-900">{t('imputations.operationCode')}</label>
              <div className="relative">
                <div className={`flex items-center w-full h-12 rounded-lg border ${
                  formErrors.operation ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                } px-4 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all overflow-hidden ${
                  !editable ? 'bg-slate-50' : ''
                }`}>
                  <span className="material-symbols-outlined text-slate-500 mr-3">search</span>
                  <input
                    type="text"
                    value={operationSearch}
                    onChange={(e) => {
                      setOperationSearch(e.target.value);
                      setShowOperationDropdown(true);
                    }}
                    onFocus={() => editable && setShowOperationDropdown(true)}
                    placeholder={t('imputations.searchCodeOrDescription')}
                    disabled={!editable}
                    className="w-full h-full bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400 disabled:cursor-not-allowed"
                  />
                  {operationSearch && editable && (
                    <button
                      type="button"
                      onClick={() => {
                        setOperationSearch('');
                        setSelectedOperation(null);
                        setFormData({ ...formData, operation: '' });
                      }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                  )}
                </div>
                
                {showOperationDropdown && editable && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {getFilteredOperations(operationSearch).map(op => (
                      <div
                        key={op.id}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            operation: op.id.toString(),
                            ...(op.imputation_project_type_choice === 'PREDEFINED' && op.default_project_type ? { gcr_prefix: op.default_project_type } : {})
                          });
                          setOperationSearch(`${op.production_line_lp} · ${op.activity}`);
                          setSelectedOperation(op);
                          setShowOperationDropdown(false);
                        }}
                        className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900">{op.production_line_lp} · {op.activity}</div>
                        <div className="text-xs text-slate-500 mt-1">{t('imputations.standardTime')}: {op.temps_standard}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedOperation && (
                <p className="text-xs text-slate-500 mt-1.5 pl-1">
                  {t('imputations.selectedOp', { activity: selectedOperation.activity, status: selectedOperation.is_active ? tCommon('active') : tCommon('inactive') })}
                </p>
              )}
              {formErrors.operation && (
                <p className="text-red-600 text-xs mt-1">{formErrors.operation[0]}</p>
              )}
            </div>

            {/* Row 3: Status & Time Logic */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Final Status */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900">{t('imputations.executionStatus')}</label>
                <div className="relative w-full">
                  <select
                    value={formData.etat_final}
                    onChange={(e) => setFormData({ ...formData, etat_final: e.target.value })}
                    required
                    disabled={!editable}
                    className={`appearance-none w-full h-12 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none cursor-pointer ${
                      !editable ? 'bg-slate-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {etatChoices.map(choice => (
                      <option key={choice.value} value={choice.value}>{choice.label}</option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none flex items-center">
                    <span className="material-symbols-outlined">expand_more</span>
                  </div>
                </div>
              </div>

              {/* Standard Time (Auto-calculated) */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  {t('imputations.standardTimeImpact')}
                  <span className="material-symbols-outlined text-[16px] text-slate-500 cursor-help" title={t('imputations.calculatedBasedOnStatus')}>info</span>
                </label>
                <div className="flex items-center w-full h-12 rounded-lg bg-slate-50 px-4 border border-transparent">
                  <span className={`font-mono font-bold tracking-wider ${
                    getTenantSubdomain() !== 'rs' && formData.etat_final === 'ECHEC' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {getTenantSubdomain() !== 'rs' && formData.etat_final === 'ECHEC' ? '50%' : '100%'}
                  </span>
                  <span className="ml-auto text-xs text-slate-500 uppercase font-medium tracking-wide">{t('imputations.calculated')}</span>
                </div>
              </div>
            </div>

            {/* Row 4: Workflow & GCR Code — hidden when reference disabled or no workflows */}
            {shouldHideWorkflowAndProjectCode ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-600">
                  {t('imputations.referenceNotEditable')}
                </p>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(() => {
                const isProjectTypeLocked = editable && selectedOperation?.imputation_project_type_choice === 'PREDEFINED' && selectedOperation?.default_project_type;
                return (
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-900">
                      Workflow
                      {isProjectTypeLocked && (
                        <span className="ml-2 text-xs font-normal text-slate-500">{t('imputations.fixedForOperation')}</span>
                      )}
                    </label>
                    <div className={`relative grid h-12 w-full rounded-lg bg-slate-50 p-1 border border-slate-200 ${
                      !editable ? 'opacity-50 cursor-not-allowed' : isProjectTypeLocked ? 'opacity-80 pointer-events-none' : ''
                    }`}
                      style={{ gridTemplateColumns: `repeat(${workflowOptions.length}, minmax(0, 1fr))` }}>
                      {workflowOptions.map((prefix) => (
                        <label key={prefix} className={`flex-1 h-full relative ${!editable || isProjectTypeLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="radio"
                            name="edit_gcr_prefix"
                            value={prefix}
                            checked={formData.gcr_prefix === prefix}
                            onChange={(e) => editable && !isProjectTypeLocked && setFormData(prev => ({ ...prev, gcr_prefix: e.target.value, gcr_code: '' }))}
                            disabled={!editable || isProjectTypeLocked}
                            className="peer sr-only"
                          />
                          <div className="relative flex h-full w-full items-center justify-center rounded-md text-sm font-medium text-slate-500 transition-colors peer-checked:text-primary">
                            {formData.gcr_prefix === prefix && editable && (
                              <motion.div
                                layoutId="editProjectTypeIndicator"
                                className="absolute inset-0 rounded-md bg-white shadow-sm"
                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                              />
                            )}
                            <span className="relative z-10">{workflowLabelByPrefix[prefix] ?? prefix}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Project Code: type-specific digits (left-pad with zeros if less). Cannot be all zeros. */}
              {!isProjectCodeOptionalForPrefix(formData.gcr_prefix) && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-slate-900">
                    {t('imputations.projectCode')} <span className="text-red-500">*</span>
                  </label>
                  <div className={`flex items-center rounded-lg border overflow-hidden focus-within:ring-1 focus-within:ring-primary focus-within:border-primary ${
                    formErrors.gcr ? 'border-red-300' : 'border-slate-200'
                  } ${!editable ? 'bg-slate-50' : 'bg-white'}`}>
                    <span className="inline-flex items-center h-12 px-4 bg-slate-100 text-slate-700 font-mono text-sm border-r border-slate-200 shrink-0">
                      {projectCodePrefixByType[formData.gcr_prefix] || ''}
                    </span>
                    <input
                      type="text"
                      inputMode={allowAlphabeticByPrefix[formData.gcr_prefix] ? 'text' : 'numeric'}
                      value={formData.gcr_code}
                      onChange={(e) => {
                        const maxLen = getProjectCodeMaxLength(formData.gcr_prefix);
                        const allowAlpha = allowAlphabeticByPrefix[formData.gcr_prefix];
                        let filtered = allowAlpha
                          ? e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, Math.max(maxLen * 2, 50))
                          : e.target.value.replace(/\D/g, '').slice(0, maxLen);
                        if (allowAlpha) filtered = filtered.toUpperCase();
                        editable && setFormData(prev => ({ ...prev, gcr_code: filtered }));
                      }}
                      required
                      maxLength={allowAlphabeticByPrefix[formData.gcr_prefix] ? Math.max(getProjectCodeMaxLength(formData.gcr_prefix) * 2, 50) : getProjectCodeMaxLength(formData.gcr_prefix)}
                      disabled={!editable}
                      placeholder={allowAlphabeticByPrefix[formData.gcr_prefix] ? t('imputations.projectCodePlaceholderAlpha') : t('imputations.projectCodePlaceholder', { count: getProjectCodeMaxLength(formData.gcr_prefix) })}
                      className={`flex-1 h-12 px-4 text-slate-900 font-mono placeholder-slate-400 outline-none ${
                        formErrors.gcr ? 'bg-red-50' : ''
                      } ${!editable ? 'cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('imputations.projectCodeHint', { count: getProjectCodeMaxLength(formData.gcr_prefix) })}
                    {allowAlphabeticByPrefix[formData.gcr_prefix] ? t('imputations.projectCodeLettersAllowed') : t('imputations.projectCodeDigitsOnly')}
                  </p>
                  {formErrors.gcr && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.gcr[0]}</p>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Row 5: Comments */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-900 flex justify-between">
                {t('imputations.comments')}
                <span className="text-xs font-normal text-slate-500">{tCommon('optional')}</span>
              </label>
              <textarea
                value={formData.commentaire}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                rows={4}
                disabled={!editable}
                placeholder={t('imputations.commentPlaceholderDetail')}
                className={`w-full min-h-[100px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 p-4 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder-slate-400 focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none resize-y ${
                  !editable ? 'bg-slate-50 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* Row 6: {t('imputations.modificationReason')} */}
            {editable && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900 flex justify-between">
                  {t('imputations.modificationReason')}
                  <span className="text-xs font-normal text-slate-500">{t('imputations.optionalRecommended').replace(/[()]/g, '')}</span>
                </label>
                <textarea
                  value={formData.modification_reason}
                  onChange={(e) => setFormData({ ...formData, modification_reason: e.target.value })}
                  rows={3}
                  placeholder={t('imputations.modificationReasonPlaceholder')}
                  className="w-full min-h-[80px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 p-4 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder-slate-400 focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none resize-y"
                />
                <p className="text-xs text-slate-500">
                  {t('imputations.modificationReasonAudit')}
                </p>
              </div>
            )}

            {/* Divider */}
            <div className="h-px w-full bg-slate-200"></div>

            {/* Row 7: Actions */}
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/employee-dashboard/imputations')}
                className="px-6 py-2.5 rounded-lg text-slate-500 hover:text-slate-700 font-medium transition-colors"
              >
                {tCommon('cancel')}
              </button>
              <button
                type="submit"
                disabled={saving || !editable}
                className="group relative px-6 py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-white font-medium shadow-md shadow-blue-500/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center gap-2">
                  {saving ? tCommon('saving') : t('imputations.saveChanges')}
                  {!saving && (
                    <span className="material-symbols-outlined text-[18px] group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditImputationPage;

